// Loading skeleton matched to the PresetMenuDetails layout — overview
// card with three field placeholders + a menu-items table skeleton with
// header row and seven body rows. Keep in sync with
// Components::Skeletons::PresetMenuDetails (Phlex) so SSR → hydration
// doesn't restart the shimmer animation.

import { Bar, DEFAULT_LABEL, PageShell } from '../Skeleton/primitives';

const ROW_COUNT = 7;

export function PresetMenuDetailsSkeleton({ label = DEFAULT_LABEL }: { label?: string }) {
  return (
    <PageShell label={label}>
      <SkeletonHeader />
      <div className="flex-1 overflow-y-auto py-8">
        <div className="w-full px-16 space-y-8">
          <OverviewSkeleton />
          <MenuItemsSkeleton />
        </div>
      </div>
      <SkeletonFooter />
    </PageShell>
  );
}

function SkeletonHeader() {
  return (
    <header className="flex-shrink-0 h-23 border-b border-dinnerplate-white">
      <div className="flex h-full w-full items-center justify-between px-12">
        <Bar className="h-5 w-[160px]" />
        <Bar className="h-[45px] w-[194px] rounded-input" />
      </div>
    </header>
  );
}

function OverviewSkeleton() {
  return (
    <section>
      <Bar className="h-[30px] w-32 mb-4" />
      <div className="rounded-input border border-dinnerplate-white bg-off-white p-6">
        <div className="flex gap-x-12">
          <OverviewField className="w-145" />
          <OverviewField className="w-92" />
          <OverviewField className="flex-1" />
        </div>
      </div>
    </section>
  );
}

function OverviewField({ className }: { className: string }) {
  return (
    <div className={className}>
      <Bar className="h-[17px] w-[72px]" />
      <div className="mt-2">
        <Bar className="h-5 w-[160px]" />
      </div>
    </div>
  );
}

function MenuItemsSkeleton() {
  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <Bar className="h-[30px] w-44" />
        <Bar className="h-5 w-6 rounded-xs" />
      </div>

      <div className="overflow-hidden rounded-input border border-dinnerplate-white bg-off-white">
        <SkeletonTableHeaderRow />
        {Array.from({ length: ROW_COUNT }).map((_, i) => (
          <SkeletonTableRow key={i} />
        ))}
      </div>
    </section>
  );
}

const ROW_GRID = 'grid items-center gap-8 px-6';
const ROW_TEMPLATE = '24px 36px 2fr 1fr 1fr 1fr 1fr 18px';

function SkeletonTableHeaderRow() {
  return (
    <div className={`${ROW_GRID} bg-dinnerplate-white-light py-4`} style={{ gridTemplateColumns: ROW_TEMPLATE }}>
      <Bar className="h-3.5 w-3.5 rounded-xs" />
      <span aria-hidden="true" />
      <Bar className="h-4 w-16" />
      <Bar className="h-4 w-16" />
      <Bar className="h-4 w-12" />
      <Bar className="h-4 w-24" />
      <Bar className="h-4 w-12" />
      <span aria-hidden="true" />
    </div>
  );
}

function SkeletonTableRow() {
  return (
    <div
      className={`${ROW_GRID} border-t border-dinnerplate-white py-4`}
      style={{ gridTemplateColumns: ROW_TEMPLATE }}
    >
      <Bar className="h-3.5 w-3.5 rounded-xs" />
      <Bar className="h-12 w-12 rounded-input" />
      <div className="flex flex-col gap-2">
        <Bar className="h-4 w-2/3" />
        <Bar className="h-3 w-1/3" />
      </div>
      <Bar className="h-6 w-20 rounded-xs" />
      <Bar className="h-6 w-20 rounded-xs" />
      <Bar className="h-4 w-32" />
      <Bar className="h-4 w-16" />
      <Bar className="h-4 w-1 rounded-xs" />
    </div>
  );
}

function SkeletonFooter() {
  return (
    <footer className="flex-shrink-0 h-23 border-t border-dinnerplate-white">
      <div className="flex h-full w-full items-center justify-end px-16">
        <Bar className="h-[45px] w-[64px] rounded-input" />
      </div>
    </footer>
  );
}
